import { Link } from "@tanstack/react-router";
import { ArrowRight, Building2 } from "lucide-react";
import { cn } from "@/frontend/lib/utils";
import { Button } from "@/frontend/components/ui/button";
import { logoUrl } from "@/frontend/utils/shared";
import {
  useDefaultSuggestedSourcesQuery,
  useMagicLinkActivityQuery,
} from "@/frontend/queries/dashboard/patients";
import { useWorkspacePersona } from "../individual/useWorkspacePersona";
import { nextStepFor } from "@/frontend/utils/dashboard/next-step";
import type { PatientSummary } from "@/server/schema/admin";
import { LOGO_LIMIT } from "@/frontend/types/dashboard/health-systems";

/**
 * The single next step for this patient, shown on Summary under patient
 * details. Fetches the workspace persona and the newest access link itself
 * rather than taking them as props. Renders nothing when there is no step.
 */
export function NextStepCard({
  patientId,
  summary,
}: {
  patientId: string;
  summary: PatientSummary;
}) {
  const { isIndividual, isLoading } = useWorkspacePersona();
  // Only an organization workspace hands out links, so an individual never pays
  // for this query.
  const { data: linkActivity } = useMagicLinkActivityQuery(patientId, {
    enabled: !isIndividual && !isLoading,
  });
  const step = isLoading
    ? null
    : nextStepFor(summary, { isIndividual, latestLink: linkActivity?.rows[0] ?? null });
  const { data: defaults } = useDefaultSuggestedSourcesQuery(!!step?.showHealthSystemLogos);

  // Nothing is more urgent than not showing the wrong persona's advice.
  if (!step) return null;

  const logoSources = (defaults?.sources ?? []).slice(0, LOGO_LIMIT);
  const isAttention = step.tone === "attention";
  const showLogos = step.showHealthSystemLogos && logoSources.length > 0;

  return (
    <div
      className={cn(
        "flex shrink-0 items-center gap-4 rounded-xl border p-5",
        isAttention
          ? "border-amber-500/30 bg-amber-500/5"
          : "border-border/70 bg-gradient-to-b from-muted/50 to-card",
      )}
    >
      <span
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-lg",
          isAttention
            ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
            : "border bg-background shadow-xs",
        )}
      >
        <step.icon className="size-4" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="text-body font-semibold">{step.title}</div>
        <p className="mt-1 max-w-2xl text-caption text-muted-foreground">{step.body}</p>

        {showLogos && (
          <div className="mt-3.5 flex flex-wrap items-center gap-2">
            {logoSources.map((source) => {
              const url = logoUrl(source.logo);
              return (
                <span
                  key={source.fhirBaseUrl}
                  title={source.name}
                  className="flex size-8 items-center justify-center overflow-hidden rounded-lg border bg-background shadow-xs"
                >
                  {url ? (
                    <img
                      src={url}
                      alt={source.name}
                      className="size-5 object-contain"
                      data-ph-no-capture
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <Building2 className="size-4 text-muted-foreground" />
                  )}
                </span>
              );
            })}
            <span className="text-meta text-muted-foreground">and 40,000 more</span>
          </div>
        )}
      </div>

      <Button asChild size="sm" className="shrink-0">
        <Link
          to={`/dashboard/patients/$patientId/${step.section}`}
          params={{ patientId }}
        >
          {step.actionLabel}
          <ArrowRight className="size-3.5" />
        </Link>
      </Button>
    </div>
  );
}
